import { JOURNEY, type Land } from "./lands";
import type { Viewport } from "./protocol";
import { fragmentShader, VERTEX_SHADER } from "./shaders";

/**
 * Renders the land backgrounds with raw WebGL2. Runs inside the worker
 * (OffscreenCanvas) or, as a fallback, on the main thread. It owns all timing:
 * ambient time, the ~1s crossfades, slow cycling through multi-land
 * identities, a 30 fps cap and adaptive internal resolution.
 */

type AnyCanvas = HTMLCanvasElement | OffscreenCanvas;

interface Scheduler {
  request(callback: (now: number) => void): number;
  cancel(handle: number): void;
}

interface Layer {
  land: Land;
  weight: number;
}

interface ProgramInfo {
  program: WebGLProgram;
  uRes: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
}

const FRAME_MS = 1000 / 30;
const FADE_MS = 1000;
const CYCLE_MS = 13000;
const STILL_TIME = 24;
const MAX_LAYERS = 3;
const MAX_PIXELS = 1_100_000;
const QUALITY = { start: 0.6, min: 0.32, max: 0.75 };
/** GPU budget per frame (ms) before resolution drops, and below which it may grow back. */
const GPU_BUDGET = { high: 7, low: 2.5 };

interface TimerExt {
  TIME_ELAPSED_EXT: number;
  GPU_DISJOINT_EXT: number;
}

export class LandscapeEngine {
  private readonly gl: WebGL2RenderingContext;
  private readonly programs = new Map<Land, ProgramInfo>();
  private readonly timer: TimerExt | null;
  private pendingQuery: WebGLQuery | null = null;
  private gpuMs = 0;

  private playlist: readonly Land[] = JOURNEY;
  private playIndex = 0;
  private cycleElapsed = 0;
  private layers: Layer[] = [];

  private viewport: Viewport = { width: 1, height: 1, dpr: 1 };
  private quality = QUALITY.start;
  private lastQualityChange = 0;
  private slowFrames = 0;

  private ambient = 0;
  private lastTick = -1;
  private lastRender = -1;
  private frameCount = 0;
  private handle = 0;
  private running = false;
  private visible = true;
  private reducedMotion = false;
  private dirty = true;
  private lost = false;
  private warmQueue: Land[] = [];

  constructor(
    private readonly canvas: AnyCanvas,
    private readonly scheduler: Scheduler,
    private readonly onFirstFrame: () => void,
  ) {
    const gl = canvas.getContext("webgl2", {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      premultipliedAlpha: true,
      preserveDrawingBuffer: false,
      powerPreference: "low-power",
    }) as WebGL2RenderingContext | null;
    if (!gl) throw new Error("WebGL2 unavailable");
    this.gl = gl;
    this.timer = gl.getExtension("EXT_disjoint_timer_query_webgl2") as TimerExt | null;

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);

    const target = canvas as EventTarget;
    target.addEventListener("webglcontextlost", (event) => {
      event.preventDefault();
      this.lost = true;
      this.stop();
    });
    target.addEventListener("webglcontextrestored", () => {
      this.lost = false;
      this.programs.clear();
      const fresh = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, fresh);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      this.dirty = true;
      this.start();
    });
  }

  setViewport(viewport: Viewport) {
    this.viewport = viewport;
    this.applySize();
  }

  setReducedMotion(reduced: boolean) {
    this.reducedMotion = reduced;
    this.dirty = true;
    this.start();
  }

  setVisible(visible: boolean) {
    this.visible = visible;
    if (visible) this.start();
    else this.stop();
  }

  /** Show these lands; several lands are cycled through slowly. Empty means the default journey. */
  setLands(lands: readonly Land[]) {
    const next = lands.length > 0 ? lands : JOURNEY;
    if (next.join() === this.playlist.join() && this.layers.length > 0) return;
    this.playlist = next;
    const current = this.target();
    const keep = current ? next.indexOf(current) : -1;
    this.playIndex = keep >= 0 ? keep : 0;
    this.cycleElapsed = 0;
    this.fadeTo(next[this.playIndex] ?? "plains");
    this.warmQueue = [...warmOrder(next)];
    this.start();
  }

  start() {
    if (this.running || !this.visible || this.lost) return;
    this.running = true;
    this.lastTick = -1;
    this.handle = this.scheduler.request(this.tick);
  }

  stop() {
    if (!this.running) return;
    this.running = false;
    this.scheduler.cancel(this.handle);
  }

  private target(): Land | undefined {
    return this.layers[this.layers.length - 1]?.land;
  }

  private fadeTo(land: Land) {
    if (this.layers.length === 0) {
      this.layers = [{ land, weight: 1 }];
      this.dirty = true;
      return;
    }
    if (this.target() === land) return;
    const existing = this.layers.findIndex((layer) => layer.land === land);
    const layer = existing >= 0 ? this.layers.splice(existing, 1)[0]! : { land, weight: 0 };
    this.layers.push(layer);
    while (this.layers.length > MAX_LAYERS) {
      let smallest = 0;
      for (let i = 1; i < this.layers.length - 1; i += 1) {
        if (this.layers[i]!.weight < this.layers[smallest]!.weight) smallest = i;
      }
      this.layers.splice(smallest, 1);
    }
    this.normalize();
    this.dirty = true;
  }

  private normalize() {
    const total = this.layers.reduce((sum, layer) => sum + layer.weight, 0);
    if (total <= 0) {
      for (const layer of this.layers) layer.weight = 0;
      this.layers[this.layers.length - 1]!.weight = 1;
      return;
    }
    for (const layer of this.layers) layer.weight /= total;
  }

  private stepFade(dt: number) {
    const top = this.layers[this.layers.length - 1];
    if (!top || this.layers.length === 1) return;
    top.weight = Math.min(1, top.weight + dt / FADE_MS);
    const others = this.layers.slice(0, -1);
    const rest = others.reduce((sum, layer) => sum + layer.weight, 0);
    const scale = rest > 0 ? (1 - top.weight) / rest : 0;
    for (const layer of others) layer.weight *= scale;
    this.layers = this.layers.filter((layer) => layer === top || layer.weight > 0.002);
    if (this.layers.length === 1) top.weight = 1;
    this.dirty = true;
  }

  private tick = (now: number) => {
    if (!this.running) return;
    this.handle = this.scheduler.request(this.tick);
    if (this.lastRender >= 0 && now - this.lastRender < FRAME_MS - 4) return;

    const dt = this.lastTick < 0 ? 0 : Math.min(now - this.lastTick, 100);
    this.lastTick = now;
    const fading = this.layers.length > 1;

    if (this.playlist.length > 1) {
      this.cycleElapsed += dt;
      if (this.cycleElapsed >= CYCLE_MS) {
        this.cycleElapsed = 0;
        this.playIndex = (this.playIndex + 1) % this.playlist.length;
        this.fadeTo(this.playlist[this.playIndex]!);
      }
    }
    this.stepFade(dt);

    if (this.reducedMotion) {
      if (!this.dirty) {
        this.warmOne();
        return;
      }
    } else {
      this.ambient += dt / 1000;
      this.adapt(now, dt, fading);
    }
    this.lastRender = now;
    this.render();
    this.dirty = false;
    if (this.frameCount === 1) this.onFirstFrame();
    else if (!fading) this.warmOne();
  };

  /** Compile one not-yet-used program per idle frame so later crossfades never hitch. */
  private warmOne() {
    const land = this.warmQueue.shift();
    if (land) this.program(land);
  }

  private adapt(now: number, dt: number, fading: boolean) {
    if (fading || now - this.lastQualityChange < 2000) return;
    let change = 1;
    if (this.timer) {
      if (this.gpuMs > GPU_BUDGET.high) change = 0.85;
      else if (this.gpuMs > 0 && this.gpuMs < GPU_BUDGET.low && this.quality < QUALITY.max)
        change = 1.08;
    } else {
      this.slowFrames =
        dt > FRAME_MS * 1.6 ? this.slowFrames + 1 : Math.max(0, this.slowFrames - 1);
      if (this.slowFrames > 20) change = 0.85;
    }
    if (change === 1) return;
    const quality = Math.min(QUALITY.max, Math.max(QUALITY.min, this.quality * change));
    if (Math.abs(quality - this.quality) < 0.005) return;
    this.quality = quality;
    this.lastQualityChange = now;
    this.slowFrames = 0;
    this.gpuMs = 0;
    this.applySize();
  }

  private applySize() {
    const { width, height, dpr } = this.viewport;
    let w = width * dpr * this.quality;
    let h = height * dpr * this.quality;
    const pixels = w * h;
    if (pixels > MAX_PIXELS) {
      const shrink = Math.sqrt(MAX_PIXELS / pixels);
      w *= shrink;
      h *= shrink;
    }
    const cw = Math.max(1, Math.round(w));
    const ch = Math.max(1, Math.round(h));
    if (this.canvas.width !== cw || this.canvas.height !== ch) {
      this.canvas.width = cw;
      this.canvas.height = ch;
    }
    this.dirty = true;
  }

  private program(land: Land): ProgramInfo | null {
    const cached = this.programs.get(land);
    if (cached) return cached;
    const gl = this.gl;
    const vs = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
    const fs = compile(gl, gl.FRAGMENT_SHADER, fragmentShader(land));
    if (!vs || !fs) return null;
    const program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.bindAttribLocation(program, 0, "aPos");
    gl.linkProgram(program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.warn(`[landscape] ${land}:`, gl.getProgramInfoLog(program));
      return null;
    }
    const info = {
      program,
      uRes: gl.getUniformLocation(program, "uRes"),
      uTime: gl.getUniformLocation(program, "uTime"),
    };
    this.programs.set(land, info);
    return info;
  }

  private render() {
    const gl = this.gl;
    this.frameCount += 1;
    const measure = this.timer !== null && !this.pendingQuery && this.frameCount % 15 === 0;
    let query: WebGLQuery | null = null;
    if (measure) {
      query = gl.createQuery();
      gl.beginQuery(this.timer!.TIME_ELAPSED_EXT, query);
    }

    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    const time = this.reducedMotion ? STILL_TIME : STILL_TIME + this.ambient;
    let accumulated = 0;
    gl.disable(gl.BLEND);
    for (const layer of this.layers) {
      const info = this.program(layer.land);
      if (!info) continue;
      accumulated += layer.weight;
      if (accumulated > layer.weight) {
        gl.enable(gl.BLEND);
        gl.blendColor(0, 0, 0, layer.weight / accumulated);
        gl.blendFunc(gl.CONSTANT_ALPHA, gl.ONE_MINUS_CONSTANT_ALPHA);
      }
      gl.useProgram(info.program);
      gl.uniform2f(info.uRes, this.canvas.width, this.canvas.height);
      gl.uniform1f(info.uTime, time);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    if (query) {
      gl.endQuery(this.timer!.TIME_ELAPSED_EXT);
      this.pendingQuery = query;
    } else if (this.pendingQuery) {
      this.readQuery();
    }
  }

  private readQuery() {
    const gl = this.gl;
    const query = this.pendingQuery!;
    if (!gl.getQueryParameter(query, gl.QUERY_RESULT_AVAILABLE)) return;
    const disjoint = gl.getParameter(this.timer!.GPU_DISJOINT_EXT) as boolean;
    if (!disjoint) {
      const ms = (gl.getQueryParameter(query, gl.QUERY_RESULT) as number) / 1e6;
      this.gpuMs = this.gpuMs === 0 ? ms : this.gpuMs * 0.6 + ms * 0.4;
    }
    gl.deleteQuery(query);
    this.pendingQuery = null;
  }
}

function warmOrder(playlist: readonly Land[]): Land[] {
  return [...playlist, ...JOURNEY.filter((land) => !playlist.includes(land))];
}

function compile(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.warn("[landscape] shader:", gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}
